import React, {
    FC,
    MutableRefObject,
    ReactElement,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'
import { useSearchParams } from 'react-router-dom'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { PaginationInfo } from '../../../../NonReact/Types/Types'
import { AdminFilters, AdminFilterValues, AdminFiltersSlotRenderArgs } from '../../Molecules/AdminFilters/AdminFilters'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn } from '../../Molecules/DataTable/DataTable'
import { displayError } from '../../../../React/Common/Utils/Messages'
import {
    GenericAdminFilterLanguage,
    GenericAdminListLanguage,
    Router as RouterLang,
    SidebarMenu as SidebarMenuLanguage,
    TelsysListLanguage
} from '../../../../../i18n'
import { DEFAULT_ROWS_PER_PAGE, UseAdminStatState, useAdminTableState } from '../../../Hooks/useAdminStatState'
import { useAdminListLegacyServletSwitch } from '../../../Hooks/useAdminListLegacyServletSwitch'
import { buildAdminListRowKey } from '../../../Utils/adminListRowKeys'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { getTelsysList } from '../../../../React/Common/API/ItemsGetters'
import {
    TelsysList as TelsysRows,
    TelsysListItem,
    TelsysListResponse,
    TelsysListRow
} from '../../../../NonReact/Types/TelSysListTypes'
import { TelSysConfigurationModal } from '../../../../React/TelSysConfiguration/Modal/TelSysConfigurationModal'
import { DEFAULT_CME_ID } from '../../../../React/Common/Constants/ServerValues'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import { SelectFilterOption } from '../../Molecules/AdminFilters/SelectFilter'
import { deleteTelSysMany } from '../../../../NonReact/common/API/EntityAPIClient'
import { TelsysListAPIFilters } from '../../../../NonReact/constants/ApiConstants'
import { ALL_VALUE, ANY_VALUE_STRING_DEFAULT } from '../../../../React/Common/Constants/DefaultValues'
import { AdminListCountLink } from '../../Atoms/AdminListCountLink/AdminListCountLink'
import { RouteID } from '../../Molecules/Header/Types/Types'
import {
    ADMIN_URL_QUERY_CME,
    ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN,
    ADMIN_URL_QUERY_PARTITION,
    ADMIN_URL_QUERY_TELEPHONY_SERVER,
    AdminListUrlBootstrap,
    buildAdminListPathWithQuery,
    parseAdminListUrlSearchParams
} from '../../../Utils/adminListUrlParams'
import { TelsysListExtraFilters, TelsysListColumnFilterValues } from './TelsysListExtraFilters'

/**
 * Routed Telephony Servers list view.
 *
 * @returns Telephony Servers list page content.
 */
export const TelsysList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(TelsysListLanguage.NAME)
    language.loadBundle(SidebarMenuLanguage.NAME)
    language.loadBundle(GenericAdminListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)

    const [telsysList, setTelsysList]: ReactState<Nullable<TelsysRows>> = useState<Nullable<TelsysRows>>()
    const {
        loading,
        setLoading,
        sort,
        setSort,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState()
    const [userInfo]: UserInfo = useUserInfo()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [isTelsysModalOpen, setTelsysModalOpen]: ReactState<boolean> = useState<boolean>(false)
    const [selectedTelsysId, setSelectedTelsysId]: ReactState<Definable<string>> = useState<Definable<string>>()
    const [telsysModalViewOnly, setTelsysModalViewOnly]: ReactState<boolean> = useState<boolean>(false)

    const [telsysColumnFilters, setTelsysColumnFilters]: ReactState<TelsysListColumnFilterValues> =
        useState<TelsysListColumnFilterValues>({
            platformType: { label: ANY_VALUE_STRING_DEFAULT, value: ALL_VALUE },
            reportLicence: { label: ANY_VALUE_STRING_DEFAULT, value: ALL_VALUE },
            moduleStatus: { label: ANY_VALUE_STRING_DEFAULT, value: ALL_VALUE }
        })

    const filterCMEnvironmentIDForModal: string = String(
        filters?.cme?.value ?? userInfo?.Scope?.CMEnvironmentID ?? DEFAULT_CME_ID
    )

    const cmeIdForCountLinks: string = filterCMEnvironmentIDForModal

    const [searchParams] = useSearchParams()
    const urlBootstrap: Nullable<AdminListUrlBootstrap> = useMemo((): Nullable<AdminListUrlBootstrap> => {
        const parsed: AdminListUrlBootstrap = parseAdminListUrlSearchParams(searchParams)

        return Object.keys(parsed).length > 0 ? parsed : null
    }, [searchParams])
    const [hasResolvedInitialUrlBootstrapLoad, setHasResolvedInitialUrlBootstrapLoad]: ReactState<boolean> =
        useState<boolean>(() => IsVarEmpty(urlBootstrap))
    const isBootstrappingFromUrl: boolean = isVarNotEmpty(urlBootstrap) && IsVarEmpty(filters)
    const isWaitingForInitialUrlBootstrapLoad: boolean =
        isVarNotEmpty(urlBootstrap) && !hasResolvedInitialUrlBootstrapLoad

    /**
     * Query inputs that affect the telephony server list API. Kept primitive so the list does not
     * refetch when {@link AdminFilters} updates unrelated fields (e.g. telephony server options).
     */
    const listCmeId: Nullable<string> = (filters?.cme?.value ?? userInfo?.Scope?.CMEnvironmentID) as Nullable<string>
    const listIncludeRecursive: boolean = filters?.includeCmeChildren ?? true
    const listSearch: string = filters?.search ?? EMPTY_STRING

    useAdminListLegacyServletSwitch(ServletCmds.ADMIN_TELSYSS, filters, userInfo?.Scope?.CMEnvironmentID)

    const listApiFilters: Definable<TelsysListAPIFilters> = useMemo((): Definable<TelsysListAPIFilters> => {
        const platformVal: Definable<string | number> = telsysColumnFilters.platformType?.value
        const licenceVal: Definable<string | number> = telsysColumnFilters.reportLicence?.value
        const statusVal: Definable<string | number> = telsysColumnFilters.moduleStatus?.value

        const platformType: Definable<readonly number[]> =
            isVarNotEmpty(platformVal) && platformVal !== ALL_VALUE ? [Number(platformVal)] : undefined
        const reportLicence: Definable<readonly string[]> = typeof licenceVal === 'string' ? [licenceVal] : undefined
        const moduleStatus: Definable<readonly number[]> =
            isVarNotEmpty(statusVal) && statusVal !== ALL_VALUE ? [Number(statusVal)] : undefined

        if (!platformType && !reportLicence && !moduleStatus) return undefined

        return {
            ...(platformType ? { platformType } : {}),
            ...(reportLicence ? { reportLicence } : {}),
            ...(moduleStatus ? { moduleStatus } : {})
        }
    }, [
        telsysColumnFilters.platformType?.value,
        telsysColumnFilters.reportLicence?.value,
        telsysColumnFilters.moduleStatus?.value
    ])

    const resetPaginationToFirstPage = useCallback((): void => {
        setPagination(([, rowsPerPage]: PaginationInfo): PaginationInfo => [0, rowsPerPage])
    }, [setPagination])

    /**
     * Fetches the telephony server rows for the current filter state.
     *
     * @returns No return value.
     */
    const getTelephonyServers = useCallback((): void => {
        if (isBootstrappingFromUrl) return

        if (IsVarEmpty(listCmeId)) {
            setHasResolvedInitialUrlBootstrapLoad(true)
            return
        }

        setLoading((currentLoading: number): number => currentLoading + 1)

        const [currentPage, rowsPerPage]: PaginationInfo = pagination
        const pageStartIndex: number = currentPage * rowsPerPage
        const searchQuery: Definable<string> = listSearch === EMPTY_STRING ? undefined : listSearch

        getTelsysList(listCmeId, listIncludeRecursive, pageStartIndex, rowsPerPage, sort, searchQuery, listApiFilters)
            .then((response: Nullable<TelsysListResponse>): void => {
                if (isMounted.current) {
                    const rows: TelsysRows =
                        response?.Data?.Items?.map(
                            (telSys: TelsysListItem, index: number): TelsysListRow => ({
                                key: buildAdminListRowKey(
                                    [listCmeId, telSys.TelSysID, telSys.Description],
                                    pageStartIndex,
                                    index
                                ),
                                TelSysID: telSys.TelSysID,
                                Description: telSys.Description ?? EMPTY_STRING,
                                ListTrialDisplay: telSys.ListTrialDisplay,
                                ListTelSysTypeDisplay: telSys.ListTelSysTypeDisplay,
                                ListLicenseDisplay: telSys.ListLicenseDisplay,
                                ListExtensionCountDisplay: telSys.ListExtensionCountDisplay,
                                ListAcdAgentCountDisplay: telSys.ListAcdAgentCountDisplay,
                                ListAppUserCountDisplay: telSys.ListAppUserCountDisplay,
                                ListPartitionCountDisplay: telSys.ListPartitionCountDisplay,
                                ListModuleStatusDisplay: telSys.ListModuleStatusDisplay
                            })
                        ) ?? []

                    setTelsysList(rows)
                    setTotalCount(response?.Data?.Total ?? 0)
                }
            })
            .catch(displayError)
            .finally((): void => {
                setHasResolvedInitialUrlBootstrapLoad(true)

                if (isMounted.current) setLoading((currentLoading: number): number => currentLoading - 1)
            })
        // Intentionally omit `filters` from deps: only list-driving primitives above to avoid refetch when AdminFilters updates hidden fields.
    }, [isBootstrappingFromUrl, listCmeId, listIncludeRecursive, listSearch, listApiFilters, pagination, sort])

    useEffect((): void => {
        setHasResolvedInitialUrlBootstrapLoad(IsVarEmpty(urlBootstrap))
    }, [urlBootstrap])

    const refreshTelsysList = useCallback((): void => {
        getTelephonyServers()
    }, [getTelephonyServers])

    const columns: DataTableColumn<TelsysListRow, keyof TelsysListRow>[] = useMemo(
        (): DataTableColumn<TelsysListRow, keyof TelsysListRow>[] => [
            {
                headerName: language.get(TelsysListLanguage.TelsysList_Column_TelSysID),
                field: 'TelSysID',
                key: 'telsys-list-id',
                sortable: true
            },
            {
                headerName: language.get(TelsysListLanguage.TelsysList_Column_Description),
                field: 'Description',
                key: 'telsys-list-description',
                sortable: true
            },
            {
                headerName: language.get(TelsysListLanguage.TelsysList_Column_Trial),
                field: 'ListTrialDisplay',
                key: 'telsys-list-trial',
                sortable: true,
                hideBelow: 'md',
                renderCell: (row: TelsysListRow): string =>
                    isStringNotEmpty(row.ListTrialDisplay)
                        ? language.get(TelsysListLanguage.TelsysList_Trial_Yes)
                        : language.get(TelsysListLanguage.TelsysList_Trial_No)
            },
            {
                headerName: language.get(TelsysListLanguage.TelsysList_Column_PlatformType),
                field: 'ListTelSysTypeDisplay',
                key: 'telsys-list-type',
                sortable: true,
                hideBelow: 'sm',
                renderCell: (row: TelsysListRow): string => row.ListTelSysTypeDisplay ?? EMPTY_STRING
            },
            {
                headerName: language.get(TelsysListLanguage.TelsysList_Column_Licence),
                field: 'ListLicenseDisplay',
                key: 'telsys-list-licence',
                sortable: true,
                hideBelow: 'md',
                renderCell: (row: TelsysListRow): string => row.ListLicenseDisplay ?? EMPTY_STRING
            },
            {
                headerName: language.get(TelsysListLanguage.TelsysList_Column_Partitions),
                field: 'ListPartitionCountDisplay',
                key: 'telsys-list-partitions',
                sortable: false,
                hideBelow: 'md',
                disableRowSelection: true,
                renderCell: (row: TelsysListRow): ReactNode => (
                    <AdminListCountLink
                        count={row.ListPartitionCountDisplay}
                        to={buildAdminListPathWithQuery(`/${RouteID.PARTITION}`, {
                            [ADMIN_URL_QUERY_CME]: cmeIdForCountLinks,
                            [ADMIN_URL_QUERY_TELEPHONY_SERVER]: String(row.TelSysID),
                            [ADMIN_URL_QUERY_PARTITION]: String(ALL_VALUE),
                            [ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN]: listIncludeRecursive ? 'true' : 'false'
                        })}
                        ariaLabel={language.get(TelsysListLanguage.TelsysList_CountLink_PartitionsAria)}
                    />
                )
            },
            {
                headerName: language.get(TelsysListLanguage.TelsysList_Column_Extensions),
                field: 'ListExtensionCountDisplay',
                key: 'telsys-list-ext',
                sortable: false,
                hideBelow: 'md',
                disableRowSelection: true,
                renderCell: (row: TelsysListRow): ReactNode => (
                    <AdminListCountLink
                        count={row.ListExtensionCountDisplay}
                        to={buildAdminListPathWithQuery(`/${RouteID.DEVICE}`, {
                            [ADMIN_URL_QUERY_CME]: cmeIdForCountLinks,
                            [ADMIN_URL_QUERY_TELEPHONY_SERVER]: String(row.TelSysID),
                            [ADMIN_URL_QUERY_PARTITION]: String(ALL_VALUE),
                            [ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN]: listIncludeRecursive ? 'true' : 'false'
                        })}
                        ariaLabel={language.get(TelsysListLanguage.TelsysList_CountLink_ExtensionsAria)}
                    />
                )
            },
            {
                headerName: language.get(TelsysListLanguage.TelsysList_Column_AcdAgents),
                field: 'ListAcdAgentCountDisplay',
                key: 'telsys-list-agents',
                sortable: false,
                hideBelow: 'md',
                disableRowSelection: true,
                renderCell: (row: TelsysListRow): ReactNode => (
                    <AdminListCountLink
                        count={row.ListAcdAgentCountDisplay}
                        to={buildAdminListPathWithQuery(`/${RouteID.AGENT}`, {
                            [ADMIN_URL_QUERY_CME]: cmeIdForCountLinks,
                            [ADMIN_URL_QUERY_TELEPHONY_SERVER]: String(row.TelSysID),
                            [ADMIN_URL_QUERY_PARTITION]: String(ALL_VALUE),
                            [ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN]: 'true'
                        })}
                        ariaLabel={language.get(TelsysListLanguage.TelsysList_CountLink_AcdAgentsAria)}
                    />
                )
            },
            {
                headerName: language.get(TelsysListLanguage.TelsysList_Column_AppUsers),
                field: 'ListAppUserCountDisplay',
                key: 'telsys-list-users',
                sortable: false,
                hideBelow: 'md',
                disableRowSelection: true,
                renderCell: (row: TelsysListRow): ReactNode => (
                    <AdminListCountLink
                        count={row.ListAppUserCountDisplay}
                        to={buildAdminListPathWithQuery(`/${RouteID.USERS}`, {
                            [ADMIN_URL_QUERY_CME]: cmeIdForCountLinks,
                            [ADMIN_URL_QUERY_TELEPHONY_SERVER]: String(row.TelSysID),
                            [ADMIN_URL_QUERY_PARTITION]: String(ALL_VALUE),
                            [ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN]: 'true'
                        })}
                        ariaLabel={language.get(TelsysListLanguage.TelsysList_CountLink_AppUsersAria)}
                    />
                )
            },
            {
                headerName: language.get(TelsysListLanguage.TelsysList_Column_Status),
                field: 'ListModuleStatusDisplay',
                key: 'telsys-list-status',
                sortable: true,
                hideBelow: 'sm',
                renderCell: (row: TelsysListRow): string => row.ListModuleStatusDisplay ?? EMPTY_STRING
            }
        ],
        [cmeIdForCountLinks, language, listIncludeRecursive]
    )

    useEffect(getTelephonyServers, [getTelephonyServers])

    return (
        <div className='telsys-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_telsys}></PageTitle>
            <div className='telsys-list-table-container admin-table-list-container'>
                <AdminFilters
                    localeText={{
                        cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
                        includeChildCmeLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL
                        ),
                        searchLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL)
                    }}
                    urlBootstrap={urlBootstrap}
                    onChange={(values: AdminFilterValues): void => setFilters(values)}
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: false,
                        partition: false,
                        crmType: false,
                        scope: false,
                        codeType: false,
                        userType: false,
                        includeCmeChildren: true,
                        search: true,
                        includeRefresh: true
                    }}
                    additionalFilterSkeletonCount={3}
                    additionalFilters={({
                        isLoading,
                        filterValues,
                        onSelectFilterChange
                    }: AdminFiltersSlotRenderArgs): ReactElement => ( // NOSONAR
                        <TelsysListExtraFilters
                            language={language}
                            isLoading={isLoading}
                            filterValues={filterValues}
                            onSelectFilterChange={onSelectFilterChange}
                            values={telsysColumnFilters}
                            onPlatformTypeChange={(option: Nullable<SelectFilterOption>): void => {
                                setTelsysColumnFilters(
                                    (current: TelsysListColumnFilterValues): TelsysListColumnFilterValues => ({
                                        ...current,
                                        platformType: option
                                    })
                                )
                                resetPaginationToFirstPage()
                            }}
                            onReportLicenceChange={(option: Nullable<SelectFilterOption>): void => {
                                setTelsysColumnFilters(
                                    (current: TelsysListColumnFilterValues): TelsysListColumnFilterValues => ({
                                        ...current,
                                        reportLicence: option
                                    })
                                )
                                resetPaginationToFirstPage()
                            }}
                            onModuleStatusChange={(option: Nullable<SelectFilterOption>): void => {
                                setTelsysColumnFilters(
                                    (current: TelsysListColumnFilterValues): TelsysListColumnFilterValues => ({
                                        ...current,
                                        moduleStatus: option
                                    })
                                )
                                resetPaginationToFirstPage()
                            }}
                        />
                    )}
                    onRefresh={getTelephonyServers}
                />
                <DataTable<TelsysListRow, keyof TelsysListRow>
                    permissionName={AdminBackendPermissionNames.TEL_SYS}
                    localeText={{
                        addButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Add),
                        viewButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_View),
                        changeButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Change),
                        deleteButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Delete),
                        selectionActive: (count: number): string =>
                            language.getInterpolated(GenericAdminListLanguage.GenericAdminList_Footer_Selected, count),
                        noRows: language.get(GenericAdminListLanguage.GenericAdminList_Footer_NoRows)
                    }}
                    columns={columns}
                    data={telsysList ?? []}
                    defaultRowsPerPageOptions={[10, 25, 50, 100]}
                    defaultRowsPerPage={DEFAULT_ROWS_PER_PAGE}
                    totalCount={totalCount}
                    isLoading={loading > 0 || isWaitingForInitialUrlBootstrapLoad}
                    paginationServer
                    sortServer
                    isSelectable
                    colIndexToTriggerClick={1}
                    onToolbarAddClick={(): void => {
                        setSelectedTelsysId(undefined)
                        setTelsysModalViewOnly(false)
                        setTelsysModalOpen(true)
                    }}
                    onToolbarChangeClick={([selectedItem]: TelsysListRow[]): void => {
                        setSelectedTelsysId(String(selectedItem.TelSysID))
                        setTelsysModalViewOnly(false)
                        setTelsysModalOpen(true)
                    }}
                    onToolbarViewClick={([selectedItem]: TelsysListRow[]): void => {
                        setSelectedTelsysId(String(selectedItem.TelSysID))
                        setTelsysModalViewOnly(true)
                        setTelsysModalOpen(true)
                    }}
                    onToolbarDeleteClick={(selectedItems: TelsysListRow[]): void => {
                        setLoading((currentLoading: number): number => currentLoading + 1)
                        deleteTelSysMany(selectedItems.map((row: TelsysListRow): number => row.TelSysID))
                            .then((): void => {
                                getTelephonyServers()
                            })
                            .catch((error: Error): void => {
                                displayError(error)
                            })
                            .finally((): void => {
                                if (isMounted.current)
                                    setLoading((currentLoading: number): number => currentLoading - 1)
                            })
                    }}
                    onSortChange={setSort}
                    onRowsPerPageChange={(currentPage: number, newRowsPerPage: number): void =>
                        setPagination([currentPage, newRowsPerPage])
                    }
                    onPageChange={(newPage: number, rowsPerPage: number): void => setPagination([newPage, rowsPerPage])}
                />
                {isTelsysModalOpen ? (
                    <TelSysConfigurationModal
                        key={`${selectedTelsysId ?? 'new'}-${telsysModalViewOnly ? 'v' : 'e'}`}
                        filterCMEnvironmentID={filterCMEnvironmentIDForModal}
                        refreshAction={refreshTelsysList}
                        selectedTelsys={selectedTelsysId}
                        viewOnly={telsysModalViewOnly}
                        onModalExited={(): void => {
                            setTelsysModalOpen(false)
                            setSelectedTelsysId(undefined)
                        }}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
